import React, { useState, useEffect, useRef } from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";
import { ToastContainer } from "react-toastify";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import authHeader from "../../../services/auth-header";
import { API } from "../../../backend";
import Helper from "../../../Helper";
import lineOfBusinessService from "../../../services/company/lineofbusinessService";
import subcompanyService from "../../../services/company/subcompanyService";
import { getTrans as t } from "../../../Localization/translations";

function AllExpenseReport() {

    const tableRef = useRef();
    const navigate = useNavigate();
    const [subcompany, setSubcompany] = useState("");
    const [lineOfBusiness, setLineOfBusiness] = useState("");
    const [subcompanyOptions, setSubcompanyOptions] = useState([]);
    const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState([]);
    const [state1, setState] = useState({ isPaneOpen: false });

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");


    // ------------------------------
    // Fetch Filters
    // ------------------------------
    useEffect(() => {
        subcompanyService.getSubcompany()
            .then(res => setSubcompanyOptions(res.data || []));

        lineOfBusinessService.getLineOfBusiness()
            .then(res => setLineOfBusinessOptions(res.data || []));
    }, []);

    const buildParams = () => {
        const params = new URLSearchParams();

        if (subcompany)
            params.append("subcompany", subcompany);

        if (lineOfBusiness)
            params.append("line_of_business", lineOfBusiness);

        if (fromDate)
            params.append("from_date", Helper.DatePicker(fromDate));

        if (toDate)
            params.append("to_date", Helper.DatePicker(toDate));

        return params.toString();
    };


    // ------------------------------
    // Fetch ALL Data for Export
    // ------------------------------
    const fetchAllExpenseData = async () => {
        let allData = [];
        let page = 1;
        const pageSize = 100;

        while (true) {

            const params = new URLSearchParams();

            params.append("per_page", pageSize);
            params.append("page", page);

            if (subcompany)
                params.append("subcompany", subcompany);

            if (lineOfBusiness)
                params.append("line_of_business", lineOfBusiness);

            if (fromDate)
                params.append("from_date", Helper.DatePicker(fromDate));

            if (toDate)
                params.append("to_date", Helper.DatePicker(toDate));

            const url = `${API}expenses/?${params.toString()}`;

            const response = await fetch(url, { headers: authHeader() });
            const result = await response.json();

            const data = result.results || result.data || [];
            if (!data.length) break;

            allData = [...allData, ...data];

            const total = result.count || result.total || 0;
            if (allData.length >= total) break;

            page++;
        }

        return allData;
    };


    return (
        <div className="page-wrapper">
            <div className="container-fluid py-4 pt-2">
                <div className="row py-2 mt-2"></div>
                <ToastContainer />
                <div className="col-md-12">
                    <MaterialTable
                        tableRef={tableRef}
                        style={{
                            backgroundColor: "var(--bs-table-bg)",
                            borderBottomWidth: "1px",
                            boxShadow:
                                "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                        }}
                        title={<h5 className="mb-0">{t("Expenses Report")}</h5>}

                        columns={[
                            {
                                title: t("Expense Number"),
                                field: "expense_number",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                                render: (rowData) => (
                                    <>
                                        <div className="d-xl-none">Expense No</div>
                                        <div
                                            className="hyperlink"
                                            onClick={() => navigate("/expenses/view", { state: rowData.id })}
                                        >
                                            {rowData.expense_number}
                                        </div>
                                    </>
                                ),

                            },


                            {
                                title: t("Expense Date"),
                                field: "expense_date",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                                render: row =>
                                    row.expense_date
                                        ? Helper.DateNormal(row.expense_date)
                                        : "",
                            },
                            {
                                title: t("Category"),
                                field: "expense_category",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                            },

                            {
                                title: t("Pay To"),
                                field: "pay_to",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                            },
                            {
                                title: t("Vendor"),
                                field: "vendor_name",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                            },
                            {
                                title: t("Employee"),
                                field: "employee_name",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                            },
                            {
                                title: t("Sub Company"),
                                field: "subcompany_name",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                            },
                            {
                                title: t("Line of Business"),
                                field: "line_of_business_name",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                            },
                            {
                                title: t("Amount"),
                                field: "amount",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                                render: row => Helper.Amount(row.amount || 0),

                            },
                            {
                                title: t("Status"),
                                field: "status",
                                headerStyle: { fontWeight: "bold" },
                                cellStyle: { fontSize: 12 },
                            },
                        ]}


                        data={query =>
                            new Promise(resolve => {

                                const params = new URLSearchParams();

                                // ✅ Safe page handling
                                const pageNumber = Number.isFinite(query.page)
                                    ? query.page + 1
                                    : 1;

                                params.append("per_page", query.pageSize || 10);
                                params.append("page", pageNumber);
                                params.append("search", query.search || "");

                                if (subcompany)
                                    params.append("subcompany", subcompany);

                                if (lineOfBusiness)
                                    params.append("line_of_business", lineOfBusiness);

                                if (fromDate)
                                    params.append("from_date", Helper.DatePicker(fromDate));

                                if (toDate)
                                    params.append("to_date", Helper.DatePicker(toDate));

                                const url = `${API}expenses/?${params.toString()}`;


                                fetch(url, { headers: authHeader() })
                                    .then(response => response.json())
                                    .then(result => {

                                        const data = result.results || result.data || [];
                                        const total =
                                            result.count ??
                                            result.total ??
                                            0;

                                        const currentPage =
                                            result.current_page ??
                                            result.page ??
                                            pageNumber;

                                        resolve({
                                            data: data,
                                            page: currentPage - 1, // 0-based index
                                            totalCount: total,
                                        });
                                    })
                                    .catch(() => {
                                        resolve({
                                            data: [],
                                            page: 0,
                                            totalCount: 0,
                                        });
                                    });
                            })
                        }



                        localization={{
                            toolbar: {
                                searchPlaceholder: t("Search Expenses"),
                            },
                        }}

                        components={{
                            Toolbar: props => (
                                <div className="MuiToolbar-root MTableToolbar-spacer-21 my-temp-class">
                                    <MTableToolbar {...props} />
                                    <span
                                        onClick={() =>
                                            setState({ isPaneOpen: true })
                                        }
                                        style={{
                                            paddingRight: "10px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <img
                                            src={
                                                process.env.PUBLIC_URL +
                                                "/static/images/ic-filter.svg"
                                            }
                                            alt=""
                                        />
                                    </span>
                                </div>
                            ),
                        }}

                        options={{
                            headerStyle: {
                                backgroundColor: "white",
                                color: "#245C7F",
                                fontSize: 11,
                            },
                            rowStyle: (rowData, index) => ({
                                backgroundColor:
                                    index % 2 ? "#fff" : "var(--bs-table-accent-bg)",
                            }),
                            sorting: true,
                            search: true,
                            paging: true,
                            pageSize: 10,
                            pageSizeOptions: [10, 20, 25, 50],
                            paginationType: "stepped",
                            emptyRowsWhenPaging: false,
                            exportButton: true,
                            exportFileName: "Expenses",
                            actionsColumnIndex: -1,
                            columnsButton: true,

                            // ------------------------------
                            // CSV Export
                            // ------------------------------
                            exportCsv: async columns => {
                                const allData = await fetchAllExpenseData();
                                if (!allData.length) return;

                                const visibleColumns = columns.filter(col => !col.hidden);
                                const headers = visibleColumns.map(col => col.title);

                                const rows = allData.map(row =>
                                    visibleColumns.map(col => {
                                        if (col.field === "expense_date")
                                            return row.expense_date
                                                ? Helper.DateNormal(row.expense_date)
                                                : "";

                                        if (col.field === "amount")
                                            return Helper.Amount(row.amount || 0);

                                        return row[col.field] ?? "";
                                    })
                                );

                                const csvContent =
                                    [headers, ...rows]
                                        .map(e => e.join(","))
                                        .join("\n");

                                const blob = new Blob([csvContent], {
                                    type: "text/csv;charset=utf-8;",
                                });

                                const link = document.createElement("a");
                                link.href = URL.createObjectURL(blob);
                                link.download = "expenses.csv";
                                link.click();
                            },

                            // ------------------------------
                            // PDF Export
                            // ------------------------------
                            exportPdf: async columns => {
                                const allData = await fetchAllExpenseData();
                                if (!allData.length) return;

                                const visibleColumns = columns.filter(col => !col.hidden);
                                const doc = new jsPDF();

                                doc.setFontSize(14);
                                doc.text("Expenses Report", 14, 10);

                                const headers = visibleColumns.map(col => col.title);

                                const rows = allData.map(row =>
                                    visibleColumns.map(col => {
                                        if (col.field === "expense_date")
                                            return row.expense_date
                                                ? Helper.DateNormal(row.expense_date)
                                                : "";

                                        if (col.field === "amount")
                                            return Helper.Amount(row.amount || 0);

                                        return row[col.field] ?? "";
                                    })
                                );

                                autoTable(doc, {
                                    head: [headers],
                                    body: rows,
                                    startY: 15,
                                });

                                doc.save("expenses.pdf");
                            },
                        }}
                    />

                    {/* FILTER PANEL */}
                    <SlidingPane
                        isOpen={state1.isPaneOpen}
                        title={t("Filter By")}
                        onRequestClose={() =>
                            setState({ isPaneOpen: false })
                        }
                    >
                        <div className="mb-3">
                            <label className="col-form-label">{t("Sub Company")}</label>
                            <select
                                className="form-select"
                                value={subcompany}
                                onChange={e => setSubcompany(e.target.value)}
                            >
                                <option value="">{t("Select Company")}</option>
                                {subcompanyOptions.map(sub => (
                                    <option key={sub.id} value={sub.id}>
                                        {sub.sub_company_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mb-3">
                            <label className="col-form-label">{t("Line of Business")}</label>
                            <select
                                className="form-select"
                                value={lineOfBusiness}
                                onChange={e => setLineOfBusiness(e.target.value)}
                            >
                                <option value="">
                                    {t("Select Line of Business")}
                                </option>
                                {lineOfBusinessOptions.map(lob => (
                                    <option key={lob.id} value={lob.id}>
                                        {lob.lob_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mb-3">
                            <label className="col-form-label">{t("From Date")}</label>
                            <DatePicker
                                className="form-control datepick"
                                selected={fromDate}
                                onChange={setFromDate}
                                name="date"
                                placeholderText={"mm/dd/yyyy"}
                                showYearDropdown
                                scrollableYearDropdown
                                showMonthDropdown
                                autoComplete="off"
                                dropdownMode="select"
                            />

                            <label className="col-form-label">{t("To Date")}</label>
                            <DatePicker
                                className="form-control datepick"
                                selected={toDate}
                                onChange={setToDate}
                                name="date"
                                placeholderText={"mm/dd/yyyy"}
                                showYearDropdown
                                scrollableYearDropdown
                                showMonthDropdown
                                autoComplete="off"
                                dropdownMode="select"
                            />
                        </div>


                        <button
                            className="btn bg-gradient-secondary"
                            onClick={() => {
                                setSubcompany("");
                                setLineOfBusiness("");
                                setFromDate(null);
                                setToDate(null);
                                tableRef.current.onQueryChange();
                            }}
                        >
                            {t("Reset")}
                        </button>

                        <button
                            className="btn btn-primary ms-2"
                            onClick={() =>
                                tableRef.current.onQueryChange()
                            }
                        >
                            {t("Apply")}
                        </button>
                    </SlidingPane>
                </div>
            </div>
        </div>
    );
}

export default AllExpenseReport;
